/**
 * Utility functions for color handling in the catalog creator
 */

/**
 * Converts various color formats to hex string
 * @param {*} colorValue - Color value from ColorPicker or other sources
 * @returns {string|null} Hex color string or null
 */
export const convertColorToHex = (colorValue) => {
    if (!colorValue) return null;

    // If already a hex string, return it
    if (typeof colorValue === 'string' && colorValue.startsWith('#')) {
        return colorValue;
    }

    // If it's an Ant Design ColorPicker object
    if (colorValue.toHexString) {
        return colorValue.toHexString();
    }

    // If it's an object with hex property
    if (colorValue.hex) {
        return colorValue.hex;
    }

    // Fallback: return original value
    return colorValue;
};

/**
 * Checks if an object is a serialized color object from Firestore
 * @param {*} obj - Object to check
 * @returns {boolean} True if the object is a serialized color
 */
export const isSerializedColor = (obj) => {
    return obj && 
           typeof obj === 'object' && 
           obj._isSerializedColor === true;
};

/**
 * Deserializes a color object from Firestore into a format that Ant Design can use
 * @param {*} colorValue - Color value from Firestore
 * @returns {string} A hex color string
 */
export const deserializeColor = (colorValue) => {
    // If null or undefined, return a default color
    if (colorValue === null || colorValue === undefined) {
        return '#46518a'; // Default primary color
    }
    
    // If it's a serialized color object, extract the hex value
    if (isSerializedColor(colorValue)) {
        // If the hex value is empty, use a default color based on the context
        if (!colorValue.hex) {
            // Try to determine which color this is based on context clues
            if (colorValue._colorType === 'primary') {
                return '#46518a'; // Default primary color
            } else if (colorValue._colorType === 'secondary') {
                return '#929ACC'; // Default secondary color
            } else if (colorValue._colorType === 'background') {
                return '#FFFFFF'; // Default background color
            } else if (colorValue._colorType === 'action') {
                return '#000000'; // Default action button color
            }
            return '#46518a'; // Default to primary color if type unknown
        }
        return colorValue.hex;
    }
    
    // If it's already a string, return it
    if (typeof colorValue === 'string') {
        return colorValue.startsWith('#') ? colorValue : `#${colorValue}`;
    }
    
    // Try to convert using the existing function
    const converted = convertColorToHex(colorValue);
    if (converted) {
        return converted;
    }
    
    // Fallback to a default color
    return '#46518a';
};
